import type { PreferenceStoragePort } from '@ingenico/util-mfe-shell';
import {
  AutorizationCodeServiceSubjectIt,
  CurrentUserService,
  getAndAssertCurrentUser,
  UserPreferencesStorageIt,
} from '@ingenico/util-mfe-shell';
import { useBehaviorObservable } from '@ingenico/react-utils/hooks';
import type { AuthorizationCodeService } from '@ingenico/service-authentication';
import { logError } from '@ingenico/util-common';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs';
import { DeviceManagementSpiAdapter } from '../../hexa/adapters/deviceManagementSpi.adapter';
import { appContainer } from '../../hexa/appContainer.const';
import { publishNavigationPrefix } from '../../utils/navigationPrefix.util';

export function useRootVM() {
  const deviceManagementSpi = useMemo(() => appContainer.get(DeviceManagementSpiAdapter), []);
  const { organizationName } = useParams<{ organizationName: string }>();
  const currentPageEntityLabelSubject = useMemo(
    () => deviceManagementSpi.getCurrentPageEntityLabelSubject(),
    [deviceManagementSpi],
  );
  const headerBarCustomBackgroundColorSubject = useMemo(
    () => deviceManagementSpi.getHeaderBarCustomBackgroundColorSubject(),
    [deviceManagementSpi],
  );

  const headerMobileInformationSubject = useMemo(
    () => deviceManagementSpi.getHeaderMobileInformationSubject(),
    [deviceManagementSpi],
  );

  useEffect(() => {
    if (organizationName) {
      publishNavigationPrefix(organizationName);
    }
  }, [organizationName]);

  const navigate = useNavigate();
  const currentUser$ = useMemo(() => getAndAssertCurrentUser(appContainer), []);
  const currentUser = useBehaviorObservable(currentUser$);

  const cleanUpPreferencesSessionStorage = useCallback(() => {
    const preferencesStorage = appContainer.get<PreferenceStoragePort>(UserPreferencesStorageIt);
    preferencesStorage
      .cleanup(currentUser?.id)
      .then(() => void 0)
      .catch(logError);
  }, [currentUser?.id]);

  const logoutFn = useCallback(() => {
    if (!appContainer.isBound(AutorizationCodeServiceSubjectIt)) {
      // eslint-disable-next-line no-console
      console.warn('Cannot logout: Auth is not initialized, are you running in mock mode?');
      return;
    }
    const authService = appContainer.get<BehaviorSubject<AuthorizationCodeService>>(
      AutorizationCodeServiceSubjectIt,
    ).value;

    cleanUpPreferencesSessionStorage();

    authService
      .logout()
      .pipe(
        tap(() => {
          cleanUpPreferencesSessionStorage();
        }),
      )
      .subscribe({
        // eslint-disable-next-line no-console
        error: (err) => console.error('Logout failed', err),
      });
  }, [cleanUpPreferencesSessionStorage]);

  const handleMyProfileFn = useCallback(() => {
    void navigate(`users/${currentUser?.id}`);
  }, [currentUser?.id, navigate]);

  return {
    currentPageEntityLabelSubject,
    headerBarCustomBackgroundColorSubject,
    headerMobileInformationSubject,
    organizationName,
    deviceManagementSpi,
    logoutFn,
    handleMyProfileFn,
    currentUser,
  };
}
